// plugins/notification.ts

import Vue from 'vue';
import { Plugin } from '@nuxt/types';
import NotificationService from '@root/modules/customer/services/Notification.service';

export interface NotificationPluginState {
  readonly unreadCount: number;
  readonly showProfileDot: boolean;
  initListener(): void;
  cleanup(): void;
}

declare module 'vue/types/vue' {
  interface Vue {
    $notifications: NotificationPluginState;
  }
}

const notificationPlugin: Plugin = (ctx, inject) => {
  let unsubscribeNotifications: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const state = Vue.observable({
    unreadCount: 0,
    showProfileDot: false,
  });

  const cleanup = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (unsubscribeNotifications) {
      unsubscribeNotifications();
      unsubscribeNotifications = null;
    }
    state.unreadCount = 0;
    state.showProfileDot = false;
  };

  const initListener = () => {
    cleanup();

    timer = setTimeout(async () => {
      const serviceState = await NotificationService.init();
      state.unreadCount = serviceState.unreadCount;
      state.showProfileDot = serviceState.showProfileDot;

      unsubscribeNotifications = NotificationService.subscribe((updatedState) => {
        state.unreadCount = updatedState.unreadCount;
        state.showProfileDot = updatedState.showProfileDot;
      });
    }, 1000);
  };

  ctx.store.watch(
    (storeState: any) => storeState.piano?.isLoggedIn,
    (isLoggedIn: boolean) => {
      if (isLoggedIn) {
        initListener();
      } else {
        cleanup();
      }
    },
    { immediate: true }
  );

  inject('notifications', {
    get unreadCount() {
      return state.unreadCount;
    },
    get showProfileDot() {
      return state.showProfileDot;
    },
    initListener,
    cleanup,
  } as NotificationPluginState);
};

export default notificationPlugin;
